import { Box, Chip, Grid } from "@mui/material";
import { settings } from "app";
import {
  Datagrid,
  FunctionField,
  Labeled,
  NumberField,
  ReferenceField,
  SelectField,
  TextField,
  useRecordContext,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { NationalityType } from "utils/choices";
import NestedItems from "./NestedItems";
import RowActions from "./RowActions";
import { useCollectionActions } from "./CollectionContext";
import ElectiveGroupDisciplineFields from "../forms/ElectiveGroupDisciplineFields";
import ElectiveGroupScheduleFields from "../forms/ElectiveGroupScheduleFields";

const YesNoChip = ({ value }) =>
  value ? (
    <Chip
      label="Да"
      color="success"
      size="small"
    />
  ) : (
    <Chip
      label="Не"
      variant="outlined"
      size="small"
    />
  );

const ElectiveGroupExpand = () => {
  const row = useRecordContext();
  const { items, saving, updateNestedAt } = useCollectionActions();

  if (!row) return null;

  const item = items[row.rowIndex];

  return (
    <Box sx={{ p: 1 }}>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Общо лекции">
            <NumberField
              source="totalLectures"
              textAlign="left"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Общо упражнения">
            <NumberField
              source="totalLabs"
              textAlign="left"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Изучава се в повече от един курс">
            <FunctionField render={group => <YesNoChip value={group.studyInMultipleCourses} />} />
          </Labeled>
        </Grid>
      </Grid>

      <NestedItems
        title="Дисциплини в групата"
        singular="дисциплина"
        rowPrefix={`electiveGroups#${row.rowIndex}.disciplines`}
        items={item?.disciplines}
        FormFields={ElectiveGroupDisciplineFields}
        formProps={{ excludeIds: item?.disciplines?.map(discipline => discipline.disciplineId) }}
        saving={saving}
        onSave={next => updateNestedAt(row.rowIndex, "disciplines", next)}
      >
        <ReferenceField
          source="disciplineId"
          reference={administrationRequests.getDisciplinesSelect()}
          label="Дисциплина"
          link={false}
          sortable={false}
          emptyText="-"
        >
          <TextField
            source="name"
            emptyText="-"
          />
        </ReferenceField>
      </NestedItems>

      <NestedItems
        title="Разписание"
        singular="разписание"
        rowPrefix={`electiveGroups#${row.rowIndex}.schedules`}
        items={item?.schedules}
        FormFields={ElectiveGroupScheduleFields}
        saving={saving}
        onSave={next => updateNestedAt(row.rowIndex, "schedules", next)}
      >
        <NumberField
          source="course"
          label="Курс"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
        <NumberField
          source="semester"
          label="Семестър"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
      </NestedItems>
    </Box>
  );
};

const ElectiveGroupsGrid = ({ collection }) => (
  <Datagrid
    bulkActionButtons={false}
    rowClick={false}
    expandSingle
    expand={<ElectiveGroupExpand />}
    sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
  >
    <TextField
      source="nameBg"
      label="Име на български"
      sortable={false}
      emptyText="-"
    />
    <TextField
      source="nameEn"
      label="Име на английски"
      sortable={false}
      emptyText="-"
    />
    <SelectField
      source="nationality"
      choices={NationalityType}
      label="Националност"
      sortable={false}
      emptyText="-"
    />
    <ReferenceField
      source="instructionLanguageId"
      reference={administrationRequests.getInstructionLanguagesSelect()}
      label="Език на преподаване"
      link={false}
      sortable={false}
      emptyText="-"
    >
      <TextField
        source="name"
        emptyText="-"
      />
    </ReferenceField>
    <NumberField
      source="electionCount"
      label="Брой избираеми"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <FunctionField
      label="Задължително избираеми"
      sortable={false}
      render={group => <YesNoChip value={group.isRequired} />}
    />
    <FunctionField
      label="С оценка"
      sortable={false}
      render={group => <YesNoChip value={group.hasScoring} />}
    />
    <NumberField
      source="totalHours"
      label="Общо часове"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <NumberField
      source="credits"
      label="Кредити"
      sortable={false}
      textAlign="left"
      emptyText="-"
    />
    <FunctionField
      label=""
      sortable={false}
      render={() => <RowActions collection={collection} />}
    />
  </Datagrid>
);

export default ElectiveGroupsGrid;
